// SPDX-License-Identifier: BUSL-1.1

/**
 * React hook for consuming SSE session event streams.
 *
 * Connects to the /api/sessions/:id/stream SSE endpoint and maintains
 * a rolling buffer of recent events plus running cost totals.
 *
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { getStoredToken } from '@/lib/auth';

export interface StreamEvent {
  event: string;
  data: string;
  timestamp: number;
}

interface SessionStreamState {
  connected: boolean;
  events: StreamEvent[];
  totalCost: number;
  error: string | null;
}

/** Max events retained in the rolling buffer. */
const MAX_EVENTS = 100;

/** Exponential backoff delays (ms) for reconnect attempts. */
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000];

/**
 * Subscribe to real-time session events via SSE.
 *
 * EventSource does not support custom headers (Authorization), so we
 * fall back to fetch-based SSE consumption with the stored API token.
 * Non-abort connection failures are retried with exponential backoff.
 */
export function useSessionStream(
  sessionId: string | null,
  apiUrl: string,
) {
  const [state, setState] = useState<SessionStreamState>({
    connected: false,
    events: [],
    totalCost: 0,
    error: null,
  });
  const abortRef = useRef<AbortController | null>(null);

  const connect = useCallback(() => {
    if (!sessionId) return;

    const token = getStoredToken();
    const controller = new AbortController();
    abortRef.current = controller;

    const url = `${apiUrl}/sessions/${sessionId}/stream`;

    const run = async (attempt = 0) => {
      try {
        const res = await fetch(url, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          const text = await res.text().catch(() => '');
          setState((s) => ({
            ...s,
            connected: false,
            error: `HTTP ${res.status}: ${text}`,
          }));
          return;
        }

        setState((s) => ({ ...s, connected: true, error: null }));

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';

          for (const raw of frames) {
            if (!raw.trim() || raw.trim().startsWith(':')) continue;

            let event = 'message';
            const dataLines: string[] = [];

            for (const line of raw.split('\n')) {
              if (line.startsWith('event:'))
                event = line.slice(6).trim();
              else if (line.startsWith('data:'))
                dataLines.push(line.slice(5).trim());
            }

            const data = dataLines.join('\n');

            if (event === 'budget.status') {
              try {
                const parsed = JSON.parse(data);
                setState((s) => ({ ...s, totalCost: parsed.cost }));
              } catch {
                /* ignore malformed budget */
              }
              continue;
            }

            setState((s) => ({
              ...s,
              events: [
                ...s.events.slice(-(MAX_EVENTS - 1)),
                { event, data, timestamp: Date.now() },
              ],
            }));
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
        setState((s) => ({
          ...s,
          connected: false,
          error: `Connection lost — retrying in ${delay / 1_000}s`,
        }));
        setTimeout(() => {
          if (!controller.signal.aborted) run(attempt + 1);
        }, delay);
        return;
      } finally {
        setState((s) => ({ ...s, connected: false }));
      }
    };

    run();
  }, [sessionId, apiUrl]);

  useEffect(() => {
    connect();
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [connect]);

  const disconnect = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((s) => ({ ...s, connected: false }));
  }, []);

  return { ...state, disconnect };
}
